import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Currency } from '@app/model/currency';
import {
  LineItem,
  Market,
  PaymentMethod,
  PaymentRequestDto,
} from '@app/model/payment-request/payment-request.dto';
import { SERVER_BASE_URL } from 'src/environments/environment';
import { Router } from '@angular/router';
import { LoanOption } from '@app/route/shared/LoanOption';
import { StandardCheckoutResponse } from '../user-service/user.service';
import {
  AtRoute,
  DeRoute,
  FiRoute,
  NoRoute,
  SeRoute,
} from '@assets/val/route-constants';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  locale: string = 'en-GB';
  token: string;
  isLandingError: boolean = false;
  productStateType: ZaverProduct;

  localeChanges: BehaviorSubject<string> = new BehaviorSubject<string>(
    this.locale
  );
  locale$ = this.localeChanges.asObservable();

  constructor(private http: HttpClient, private router: Router) {}

  public getCurrentLocale(): string {
    return this.locale;
  }

  public setCurrentLocale(newLocale: string): void {
    this.locale = newLocale;
    this.localeChanges.next(this.locale);
  }

  setToken(id: string): void {
    this.token = id;
  }

  getToken(): string {
    return this.token;
  }

  setLandingError(newValue: boolean): void {
    this.isLandingError = newValue;
  }

  getLandingError(): boolean {
    return this.isLandingError;
  }

  setProductStateType(productStateType?: ZaverProduct): void {
    this.productStateType = productStateType;
  }

  getProductStateType(): ZaverProduct {
    return this.productStateType;
  }

  fetchState(controlParams: StateControlParameters): Promise<CheckoutState> {
    return this.http
      .post<CheckoutState>(`${SERVER_BASE_URL}/request/state/`, controlParams, {
        withCredentials: true,
      })
      .toPromise();
  }

  /**
   * Set onlyFetch to true to not update the state and only check where the flow's at.
   */
  fetchStateNoUpdate(
    controlParams: StateControlParameters
  ): Promise<CheckoutState> {
    return this.http
      .post<CheckoutState>(
        `${SERVER_BASE_URL}/request/state/`,
        {
          ...controlParams,
          onlyFetch: true,
        },
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  //This only supports Vipps in NO and BankId in SE. Fixes need to be made in the backend so it can support more use cases (t.e. swish)
  createResumeStateToken(
    controlParams: StateControlParameters,
    resumeTokenType: ResumeTokenType
  ): Promise<string> {
    return this.http
      .post<string>(
        `${SERVER_BASE_URL}/request/state/resume/createToken`,
        {
          currentPageName: controlParams.currentPageName,
          token: controlParams.token,
          resumeTokenType: resumeTokenType,
        },
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  resume(resumeToken: string): Promise<CheckoutState> {
    let params = new HttpParams();

    params = params.append('resumeToken', resumeToken);

    return this.http
      .get<CheckoutState>(`${SERVER_BASE_URL}/request/state/resume/`, {
        withCredentials: true,
        params: params,
      })
      .toPromise();
  }

  reset(controlParams: StateControlParameters): Promise<CheckoutState> {
    return this.http
      .post<CheckoutState>(
        `${SERVER_BASE_URL}/request/state/reset`,
        controlParams,
        { withCredentials: true }
      )
      .toPromise();
  }

  registerState(controlParams: StateControlParameters): Promise<CheckoutState> {
    return this.http
      .post<CheckoutState>(
        `${SERVER_BASE_URL}/request/state/register/`,
        controlParams,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  checkUseBackendDrivenFlow(
    paymentMethod: string,
    tokenId: string
  ): Promise<boolean> {
    let params = new HttpParams()
      .set('paymentMethod', paymentMethod)
      .set('tokenId', tokenId);

    return this.http
      .get<boolean>(
        `${SERVER_BASE_URL}/request/state/checkUseBackendDrivenFlow`,
        {
          params: params,
          ...{ withCredentials: true },
        }
      )
      .toPromise();
  }

  setValue(currency: Currency): Promise<any> {
    let body: PrecheckUpdateRequest = {
      value: currency,
    };

    return this.http
      .post<PrecheckUpdateRequest>(
        `${SERVER_BASE_URL}/precheck/de/setValue/`,
        body,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  fetchPaymentRequest(token: string): Promise<PaymentRequestDto> {
    return this.http
      .get<PaymentRequestDto>(`${SERVER_BASE_URL}/request/public/${token}`, {
        withCredentials: true,
      })
      .toPromise();
  }

  getPrecheckLoanOptions(): Promise<any> {
    return this.http
      .get(`${SERVER_BASE_URL}/precheck/de/getPrecheckLoanOptions/`, {
        withCredentials: true,
      })
      .toPromise();
  }

  getPreExistingContactInfo(): Promise<any> {
    let params = new HttpParams();

    return this.http
      .get(`${SERVER_BASE_URL}/user/de/getPreExistingContactInfo/`, {
        withCredentials: true,
        params: params,
      })
      .toPromise();
  }

  getAvailablePaymentMethods(
    token: string
  ): Promise<AvailablePaymentMethods[]> {
    let params = new HttpParams();

    params = params.append('token', token);

    return this.http
      .get<AvailablePaymentMethods[]>(
        `${SERVER_BASE_URL}/request/state/availablePaymentMethods/`,
        {
          withCredentials: true,
          params: params,
        }
      )
      .toPromise();
  }

  async registerChosenMethod(methodId: string): Promise<CheckoutState> {
    const registrationParams: StateRegistration = {
      paymentMethodId: methodId,
      token: this.token,
    };

    return await this.http
      .post<CheckoutState>(
        `${SERVER_BASE_URL}/request/state/registerChosenMethod/`,
        registrationParams,
        { withCredentials: true }
      )
      .toPromise();
  }

  navigate(controlParams: StateControlParameters): Promise<CheckoutState> {
    return this.http
      .post<CheckoutState>(
        `${SERVER_BASE_URL}/request/state/navigate/`,
        controlParams,
        { withCredentials: true }
      )
      .toPromise();
  }

  resolveInterruption(
    controlParams: StateControlParameters
  ): Promise<CheckoutState> {
    return this.http
      .post<CheckoutState>(
        `${SERVER_BASE_URL}/request/state/resolveInterruption/`,
        controlParams,
        { withCredentials: true }
      )
      .toPromise();
  }

  getReadyToLeave(beenWaitingForMs: number): Promise<StayOrLeave> {
    let params = new HttpParams().append(
      'beenWaitingForMs',
      '' + beenWaitingForMs
    );
    return this.http
      .get<StayOrLeave>(`${SERVER_BASE_URL}/request/state/readyToLeave`, {
        params: params,
        withCredentials: true,
      })
      .toPromise();
  }

  applyForTokenizedPayment(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/de/applyForTokenizedPayment/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  //TODO store this on route component somehow? Seems hard to access route component from service
  // these methods are rather ugly and can most likely be streamlined quite severely
  getRouteFromState(state: CheckoutState): string {
    const marketRoutes = {
      [Market.AT]: AtRoute,
      [Market.DE]: DeRoute,
      [Market.NO]: NoRoute,
      [Market.SE]: SeRoute,
      [Market.FI]: FiRoute,
    };
    const currentMarketRoutes = marketRoutes[state.market];
    const route = currentMarketRoutes[state.currentPageInformation.pageName];
    return route;
  }

  getStepFromUrl(url: string): string {
    let route;
    const market = url.slice(0, 2).toLocaleLowerCase();
    switch (market) {
      case 'se':
        route = SeRoute;
        break;
      case 'de':
        route = DeRoute;
        break;
      case 'at':
        route = AtRoute;
        break;
      case 'no':
        route = NoRoute;
        break;
      case 'fi':
        route = FiRoute;
        break;
      default:
        console.error(
          'Attempted to fetch route from unimplemented step: ',
          market,
          url
        );
        break;
    }
    const step = Object.keys(route).find((key) => {
      return route[key] === url;
    });
    return step;
  }

  getCurrentStateLocaleBaseCode(stateLanguage: string): BaseStateLocaleCode {
    switch (stateLanguage) {
      case 'en-US':
      case 'en-GB':
        return BaseStateLocaleCode.EN;
      case 'fi-FI':
        return BaseStateLocaleCode.FI;
      case 'sv-SE':
        return BaseStateLocaleCode.SE;
      case 'de-DE':
        return BaseStateLocaleCode.DE;
      case 'de-AT':
        return BaseStateLocaleCode.AT;
      case 'nb-NO':
        return BaseStateLocaleCode.NO;
      default:
        console.error(
          'Unknown locale not supported in Zaver for business: ',
          stateLanguage
        );
        return BaseStateLocaleCode.EN;
    }
  }

  getCustomTermsUrlText(
    defaultText: string,
    customization?: CheckoutCustomizations
  ): string {
    if (customization && customization.merchantTermsUrlText) {
      return customization.merchantTermsUrlText;
    }

    return defaultText;
  }

  getCustomTaxTypeText(
    defaultText: string,
    customization?: CheckoutCustomizations
  ): string {
    if (customization && customization.merchantTaxTypeText) {
      return defaultText.replace('MwSt', customization.merchantTaxTypeText);
    }

    return defaultText;
  }

  postSwitchLanguage(
    languageSwitchJson: LanguageSwitchJson
  ): Promise<CheckoutState> {
    return this.http
      .post<CheckoutState>(
        `${SERVER_BASE_URL}/request/state/switchLanguage/`,
        languageSwitchJson,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  /**
   * Verifies that the state is in the right place and if not, forces navigation.
   */
  async syncRouteWithState() {
    const token = this.getToken();

    if (!token) {
      return;
    }

    const path: string = window.location.pathname.slice(1);

    try {
      const controlParams: StateControlParameters = {
        currentPageName: this.getStepFromUrl(path),
        token: token,
      };

      // Don't update the state in this call, we only want to know where we're really at.
      let actualFlowState = await this.fetchStateNoUpdate(controlParams);

      if (
        controlParams.currentPageName !=
        actualFlowState.currentPageInformation.pageName
      ) {
        const route = this.getRouteFromState(actualFlowState);
        this.router.navigate([route], { queryParamsHandling: 'preserve' });
      }
    } catch (err) {
      console.log(
        `Could not fetch state from path: /${path} with token: ${token}`
      );
    }
  }
}

export interface CheckoutState {
  currentPageInformation: CurrentPageInformation;
  forceNavigation: boolean;
  id: string;
  interruptionDetails: InterruptionDetails;
  language: string;
  loanOptions: LoanOption[];
  loanOfferId: string;
  market: Market;
  merchantUrls?: MerchantUrls;
  paymentInformation: PaymentInformation;
  status: CheckoutStatus;
  totalCheckoutValue: Currency;
  zaverProduct: ZaverProduct;
  checkoutCustomizations: CheckoutCustomizations;
  checkoutToken: string;
}

export interface CheckoutCustomizations {
  logoUrl: string;
  merchantTermsUrl: string;
  merchantName: string;
  merchantTermsUrlText: string;
  merchantTaxTypeText: string;
  merchantPrecheckTitle: string;
  merchantPrecheckDescription: string;
  merchantPrecheckMaxAmount: number;
}

export enum CheckoutStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  PURCHASE_CONFIRMATION_IN_PROGRESS = 'PURCHASE_CONFIRMATION_IN_PROGRESS',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

export enum ZaverProduct {
  TOKENIZED_ONBOARDING = 'TOKENIZED_ONBOARDING',
  TOKENIZED_ONBOARDING_WITH_PAYMENT = 'TOKENIZED_ONBOARDING_WITH_PAYMENT',
  ECOM_CHECKOUT = 'ECOM_CHECKOUT',
  IN_STORE_CHECKOUT = 'IN_STORE_CHECKOUT',
  TOKENIZED_PAYMENT = 'TOKENIZED_PAYMENT',
  PAYER_ONBOARDING = 'PAYER_ONBOARDING',
  PRECHECK = 'PRECHECK',
  CASHOUT = 'CASHOUT',
  B2B = 'B2B',
  SE_TOKENIZED_ONBOARDING_CREDIT = 'SE_TOKENIZED_ONBOARDING_CREDIT',
  SE_TOKENIZED_ONBOARDING_CREDIT_WITH_PAYMENT = 'SE_TOKENIZED_ONBOARDING_CREDIT_WITH_PAYMENT',
}

export interface AvailablePaymentMethods {
  id: string;
  type: string;
  lowestPossibleMonthlyPayment?: number;
  promotionalDurationDays?: number;
}

export interface MerchantUrls {
  callbackUrl?: string;
  disbursementCallbackUrl?: string;
  termsUrl?: string;
  onSuccessUrl?: string;
  onCancelUrl?: string;
}

export interface PaymentInformation {
  description: string;
  lineItems: LineItem[];
  title: string;
  directDebit: boolean;
  paymentMethod: PaymentMethod;
}

export interface CurrentPageInformation {
  pageName: AtRoute | DeRoute | SeRoute | NoRoute | FiRoute;
  homePage: boolean;
  pageSpecificParameters: any;
}

export enum ResolveType {
  CTA = 'CTA',
  CTA_BLACK = 'CTA_BLACK',
  LINK = 'LINK',
  INSTANT_REDIRECT = 'INSTANT_REDIRECT',
}

export interface StateControlParameters {
  currentPageName: string;
  token: string;
}

export enum ResumeTokenType {
  VIPPS_RESUME_STATE_TOKEN = 'VIPPS_RESUME_STATE_TOKEN'
}

export interface InterruptionControlParameters extends StateControlParameters {
  resolveType: ResolveType;
}

export interface StateRegistration {
  token: string;
  paymentMethodId: string;
}

export enum InterruptionType {
  ERROR = 'ERROR',
  REJECTION = 'REJECTION',
}
export interface InterruptionDetails {
  interuptionType: InterruptionType;
  displayBankIcon: boolean;
  heading: string;
  message: string;
  ctaLabel: string;
  ctaBlackLabel: string;
  cancelUrlOverridesCta: string;
  linkLabel: string;
  linkExternalDestination: string;
  cancelUrlOverridesLink: boolean;
  instantRedirect: boolean;
}

export interface PrecheckUpdateRequest {
  value: Currency;
}

export interface registrationBody {
  token: string;
}

export interface StayOrLeave {
  waitForMs: number;
  then: WaitThen;
  url: string;
}

export enum WaitThen {
  /**
   * WAIT implies showing a spinner and a message.
   */
  WAIT = 'WAIT',
  /**
   * STAY means there is nowhere to go to, or possibly that we have given up
   */
  STAY = 'STAY',
  /**
   * LEAVE means the payer should be sent to their next destination, e.g. the success URL
   */
  LEAVE = 'LEAVE',
}

export enum BaseStateLocaleCode {
  EN = 'en',
  FI = 'fi',
  AT = 'at',
  DE = 'de',
  NO = 'no',
  SE = 'se',
}

export interface LanguageSwitchJson {
  language: string;
  token: string;
}
